import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Title,
  CloseButton,
  ChartContainer
} from './styles';

const PositionHistoryModal = ({ isOpen, onClose, positionHistory, employeeName, onSubmit }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !positionHistory || !chartRef.current) return;

    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    // Get unique positions from history for the y-axis
    const positions = positionHistory.history.map(h => ({
      ...h.position,
      startDate: h.startDate,
      endDate: h.endDate
    }));
    
    const sortedPositions = [...positions].sort((a, b) => a.order - b.order);

    if (sortedPositions.length === 0) {
      return;
    }
    const positionLabels = sortedPositions.map(p => p.label);

    // Generate colors for each position
    const positionColors = {};
    const colorScale = d3.scaleOrdinal(d3.schemeCategory10);
    sortedPositions.forEach((position, index) => {
      positionColors[position.label] = colorScale(index);
    });

    // Transform history data for the chart
    const historyData = positionHistory.history.map(item => ({
      position: item.position.label,
      updatedAt: item.startDate,
      updatedBy: item.updatedBy || 'Unknown'
    }));

    // Sort history by date
    const sortedHistory = [...historyData].sort((a, b) => 
      new Date(a.updatedAt) - new Date(b.updatedAt)
    );

    // Set up dimensions
    const margin = { top: 40, right: 60, bottom: 60, left: 160 };
    const width = chartRef.current.clientWidth - margin.left - margin.right;
    const height = chartRef.current.clientHeight - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create scales
    const xScale = d3.scaleTime()
      .domain(d3.extent(sortedHistory, d => new Date(d.updatedAt)))
      .range([40, width])
      .nice();

    const yScale = d3.scalePoint()
      .domain(positionLabels)
      .range([height, 0])
      .padding(0.5);

    // Create line generator
    const line = d3.line()
      .x(d => xScale(new Date(d.updatedAt)))
      .y(d => yScale(d.position))
      .curve(d3.curveStepAfter);

    // Add axes
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale)
        .ticks(5)
        .tickFormat(d3.timeFormat('%b %d, %Y')))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)');

    // Add y-axis with colored labels
    const yAxis = svg.append('g')
      .call(d3.axisLeft(yScale));

    // Remove the default axis line
    yAxis.select('.domain').remove();
    // Remove the default tick lines
    yAxis.selectAll('.tick line').remove();

    // Style the y-axis labels
    yAxis.selectAll('.tick')
      .append('rect')
      .attr('x', -150)
      .attr('y', -10)
      .attr('width', 180)
      .attr('height', 20)
      .attr('rx', 4)
      .style('fill', d => `${positionColors[d]}10`)
      .style('stroke', d => positionColors[d])
      .style('stroke-width', 1);

    // Add dots before text
    yAxis.selectAll('.tick')
      .append('circle')
      .attr('cx', -135)
      .attr('cy', 0)
      .attr('r', 4)
      .style('fill', d => positionColors[d]);

    // Style the text labels
    yAxis.selectAll('.tick text')
      .attr('x', -120)
      .attr('dy', '.32em')
      .style('font-size', '12px')
      .style('fill', '#333')
      .style('font-weight', '500')
      .style('text-anchor', 'start');

    // Add grid lines
    svg.selectAll('line.horizontal-grid')
      .data(positionLabels)
      .enter()
      .append('line')
      .attr('class', 'horizontal-grid')
      .attr('x1', 40)
      .attr('x2', width)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .style('stroke', d => `${positionColors[d]}30`)
      .style('stroke-width', 1);

    // Add the line path with gradient
    const lineGradient = svg.append('linearGradient')
      .attr('id', 'position-line-gradient')
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('y1', height)
      .attr('x2', 0)
      .attr('y2', 0);

    positionLabels.forEach((position, i) => {
      lineGradient.append('stop')
        .attr('offset', `${(i / (positionLabels.length - 1)) * 100}%`)
        .attr('stop-color', positionColors[position]);
    });

    // Add the line path
    svg.append('path')
      .datum(sortedHistory)
      .attr('fill', 'none')
      .attr('stroke', 'url(#position-line-gradient)')
      .attr('stroke-width', 2)
      .attr('d', line);

    // Add dots for each data point
    svg.selectAll('.dot')
      .data(sortedHistory)
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('cx', d => xScale(new Date(d.updatedAt)))
      .attr('cy', d => yScale(d.position))
      .attr('r', (d, i) => i === sortedHistory.length - 1 ? 8 : 5)
      .style('cursor', 'pointer')
      .style('fill', d => positionColors[d.position])
      .style('stroke', '#fff')
      .style('stroke-width', (d, i) => i === sortedHistory.length - 1 ? 3 : 2);

    // Add tooltips
    const tooltip = d3.select(chartRef.current)
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('background-color', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('z-index', 1000);

    // Add hover interactions
    svg.selectAll('.dot')
      .on('mouseover', (event, d) => {
        tooltip.transition()
          .duration(200)
          .style('opacity', .9);
        tooltip.html(
          `<div style="color: ${positionColors[d.position]}">
            <strong>${d.position}</strong>
           </div>
           ${new Date(d.updatedAt).toLocaleDateString()}<br/>
           Updated by: ${d.updatedBy}`
        )
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', () => {
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });
  }, [isOpen, positionHistory]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>
            <span>{employeeName}</span>
          </Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ChartContainer ref={chartRef} />
      </ModalContent>
    </ModalOverlay>
  );
};

export default PositionHistoryModal; 